<template>
    <ul v-if="courses && courses.length > 0" class="w-full max-w-6xl mx-auto grid gap-6">
        <li 
            v-for="(block, index) in courses" 
            :key="getCourseId(block) || index"
            class="block rounded-lg overflow-hidden bg-white shadow-bento"
            :class="{
                'hover:opacity-85 transition-all duration-300': getCourse(block)?.id !== null
            }"
            :id="block.id"
        >
            <div 
                v-if="getLocator(block)"
                class="text-black px-4 py-1.5 uppercase font-bold text-xs block bg-gray-2"
                :style="{ backgroundColor: getBackgroundColor(block) + '!important' }"
            >
                {{ getLocator(block).title }}
            </div>

            <div class="max-md:flex-col w-full pt-5 pb-4 px-6 flex gap-6 items-stretch">
                <a 
                    class="block w-full max-w-96 h-auto overflow-hidden md:max-w-80" 
                    :href="getCourse(block)?.url"
                    :target="getCourse(block)?.newTab ? '_blank' : '_self'"
                >
                    <figure class="image is-loaded image--16x10 rounded-lg bg-gray-1">
                        <div class="image-wrapper">
                            <img 
                            v-if="getFeaturedImage(block)"
                                :src="getFeaturedImage(block).url"
                                :alt="getFeaturedImage(block).alt || getFeaturedImage(block).title || ''"
                                class="w-full h-full object-cover"
                            />
                        </div>
                    </figure>
                    <span class="sr-only" v-if="getCourse(block)?.newTab">Opens in a new tab</span>
                </a>

                <div class="pt-1 flex flex-col justify-between gap-2.5 flex-1">
                    <a
                        class="grid gap-3"
                        :href="getCourse(block)?.url"
                        :target="getCourse(block)?.newTab ? '_blank' : '_self'"
                    >
                        <div class="flex items-center gap-3">
                            <div class="inline-flex items-center gap-2 px-3 py-1 uppercase font-bold text-xs rounded-lg border-2 border-orange-5 bg-white">
                                <span class="block w-4 text-orange-5 [&>svg]:max-w-full [&>svg]:max-h-full">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 6.5C12 5.11929 13.1193 4 14.5 4H20C20.5523 4 21 4.44772 21 5V19C21 19.5523 20.5523 20 20 20H14.5C13.1193 20 12 18.8807 12 17.5V6.5Z" stroke="currentColor" stroke-width="2"/>
                                        <path d="M3 6C3 4.89543 3.89543 4 5 4H9C10.1046 4 11 4.89543 11 6V18C11 19.1046 10.1046 20 9 20H5C3.89543 20 3 19.1046 3 18V6Z" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                </span>
                                <span>Course</span>
                            </div>
                        </div>

                        <h3 class="heading-04 mt-1">
                            {{ getCourse(block)?.title }}
                            <span class="sr-only" v-if="getCourse(block)?.newTab">Opens in a new tab</span>
                            <Icon dir="/icons/icon-selector/" name="external" class="w-[0.75em] h-[0.75em] -translate-y-0.5 inline" v-if="getCourse(block)?.newTab" />
                        </h3>
                        <div v-if="getExcerpt(block)" v-html="getExcerpt(block).replace(/\n/g, '<br>')"></div>
                    </a>
                    <div class="w-full flex items-center"
                        :class="{
                            'justify-between': getCourse(block)?.id !== null,
                            'justify-end': getCourse(block)?.id === null
                        }">
                        <div v-if="getCourse(block)?.id !== null">
                            <button
                                class="button button--transparent"
                                aria-label="Add to favorites"
                                @click="apiAddToFavorites(getCourse(block)?.id)"
                                v-if="store.userId"
                                :disabled="idsLoading.includes(getCourse(block)?.id)"
                            >
                                <span class="block w-2">
                                    <span class="sr-only">{{ isCourseFavorited(getCourse(block)?.id) ? 'In Favorites' : 'Add to Favorites' }}</span>
                                    <Icon dir="/icons/icon-selector/" name="heart" :class="[
                                        'w-8 h-6 transition-all duration-150 fill-transparent stroke-red-default text-red-default stroke-2 hover:scale-110',
                                        isCourseFavorited(getCourse(block)?.id) ? '!fill-red-default' : '',
                                        idsLoading.includes(getCourse(block)?.id) ? 'scale-110 opacity-50 pointer-events-none' : ''
                                    ]" />
                                </span>
                            </button>
                            <button
                                class="js-tip button button--transparent w-9"
                                aria-label="Add to favorites"
                                :data-tip-content="tip"
                                role="tooltip"
                                v-else
                            >
                                <span class="block w-2">
                                    <span class="sr-only">Add to Favorites</span>
                                    <Icon dir="/icons/icon-selector/" name="heart"
                                        class="w-8 h-6 transition-all duration-150 fill-transparent stroke-red-default text-red-default stroke-2 hover:scale-110"
                                    />
                                </span>
                            </button>
                        </div>
                        <div v-if="getCourse(block)?.courseDuration" class="text-right text-gray-12">
                            {{ getCourse(block).courseDuration }}
                        </div>
                    </div>
                </div>
            </div>
        </li>
    </ul>
</template>

<script setup>
    import { onMounted, ref, watch } from 'vue';
    import Icon from './pieces/Icon.vue';
    import { store } from '../store.js';
    import axios from 'axios';
    import Tips from '../utils/tips.js';

    const idsLoading = ref([]);
    const favoritesState = ref({});

    const tip = [
        '<span class="text-xs leading-snug block">',
            '<p><a class="underline text-blue-default" href="/login">Sign in</a> to save your favorites.</p>',
            '<p>Don\'t have an account? <a class="underline text-blue-default" href="/account-creation">It\'s easy and free to sign up</a>!</p>',
        '</span>'
    ].join('');

    const props = defineProps({
        courses: {
            type: Array,
            default: () => []
        }
    });

    const csrftoken = ref({ name: '', value: '' });
    const renewCsrfToken = async () => {
        csrftoken.value = await fetch('/actions/blitz/csrf/token')
            .then(result => result.text())
            .then(result => ({ name: 'CRAFT_CSRF_TOKEN', value: result }));
    };

    const getCourse = (block) => block && block.course ? block.course : null;
    const getCourseId = (block) => getCourse(block)?.id;
    const getLocator = (block) => getCourse(block)?.locator || null;
    const getBackgroundColor = (block) => block?.backgroundColorOverride || getLocator(block)?.backgroundColor || '';
    const getFeaturedImage = (block) => {
        const cardContent = getCourse(block)?.cardContent;

        if (!cardContent || !cardContent.featuredImage) return null;
        return Array.isArray(cardContent.featuredImage) ? cardContent.featuredImage[0] : cardContent.featuredImage;
    };
    const getExcerpt = (block) => getCourse(block)?.cardContent?.excerpt;

    const isCourseFavorited = (courseId) => {
        console.log('test', courseId);
        console.log(store.favorites);
        if (!courseId) return false;
        return Object.values(store.favorites).includes(courseId);
    };

    const updateFavoritesState = () => {
        props.courses.forEach(block => {
            const courseId = getCourseId(block);
            if (courseId) {
                favoritesState.value[courseId] = isCourseFavorited(courseId);
            }
        });
    };

    const apiAddToFavorites = async (id) => {
        if (!id) return;
        
        idsLoading.value.push(id);
        await renewCsrfToken();
        let response;
        try {
            response = await axios.post('/actions/readinguniverse/user/favorites-add', {
                [csrftoken.value.name]: csrftoken.value.value,
                id: id
            });
        } catch (error) {
            console.error('Error in favorites-add:', error);
        } finally {
            idsLoading.value = idsLoading.value.filter((item) => item !== id);
        }
        
        if (response && response.data) {
            if (Array.isArray(response.data.favorites)) {
                store.updateFavorites(response.data.favorites);
            } else if (response.data.isFavorite !== undefined) {
                const isFavorite = response.data.isFavorite;
                if (isFavorite) {
                    if (!Object.values(store.favorites).includes(id)) {
                        store.favorites[id] = id;
                    }
                } else {
                    delete store.favorites[id];
                }
            }
        }
        return response?.data || [];
    };

    watch(
        () => store.favorites,
        () => {
            updateFavoritesState();
        }
    );

    onMounted(() => {
        updateFavoritesState();
        Tips.init();
    });
</script>
